import CenteredContent from 'components/CenteredContent';
import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';
import PageLayout from 'components/PageLayout';
import { useAtom, useAtomValue } from 'jotai';
import { showSuccessToast } from 'lib/ToastHelper';
import React, { Suspense, useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import {
  IntegrationState,
  authCodeAtom,
  createAccountResponseAtom,
  hostTypeAtom,
  integrationStateAtom,
} from './state';
import history from '../../../history.js';
import { useAccountsResetAtoms } from '../Accounts/state';
import { BASE_URL, State, getOAuthStateData } from '../helper';

const NewAccountIntegration = (props) => {
  const createAccountResponse = useAtomValue(createAccountResponseAtom);
  const [hostType, setHostType] = useAtom(hostTypeAtom);
  const [authCode, setAuthCode] = useAtom(authCodeAtom);
  const [integrationState, setIntegrationState] = useAtom(integrationStateAtom);
  const [state, setState] = useState<State>();
  const resetAccountsAtoms = useAccountsResetAtoms();

  // # 1. setup hostType, code and state information
  // will trigger the account creation
  useEffect(() => {
    setIntegrationState(IntegrationState.None);
    const data = getOAuthStateData();
    if (data && data.code && data.state) {
      setState(data.state);
      setHostType(data.state.hostType);
      setAuthCode(data.code);
      setIntegrationState(IntegrationState.CreatingAccount);
    }
  }, []);

  // # 2. on account creation, process response
  useEffect(() => {
    if (createAccountResponse) {
      if (createAccountResponse.success) {
        resetAccountsAtoms();
        if (state?.mode === 'revive') {
          handlePostAccountRevive();
        } else if (state?.mode === 'new') {
          showSuccessToast('Account is successfully integrated.');
          history.push(
            `/${BASE_URL}/sast?account=${createAccountResponse?.id}&t=add_repos`,
          );
        }
      } else {
        // setIntegrationState(IntegrationState.Failed);
      }
    }
  }, [createAccountResponse]);

  const handlePostAccountRevive = () => {
    showSuccessToast('Account is successfully re-activated.');
    history.push(`/${BASE_URL}/dashboard?t=account#sast2`);
  };

  /**
   * Flow/Steps required:
   * 1. validate the oauth data code, state values
   * 2. request the api to setup/configure account
   * 3. if account already exists, then show a message account is re-activated.
   * 4. load all repositories list , and already connected repositories against this account
   * 5. show a tree of repositories, and allow selection of repositories
   * 6. allow saving/adding of those repositories.
   * 7. show a confirmation message, and a link to the repository listing page
   * the icons display for repositories into a function
   *
   *
   */

  return (
    <Suspense fallback={<LoadingScreen />}>
      <ErrorBoundary>
        <PageLayout id="sast_new_account" heading="New Account">
          {!createAccountResponse && (
            <>
              {integrationState &&
                integrationState === IntegrationState.None && (
                  <LoadingScreen mask={true} />
                )}
              {integrationState &&
                integrationState === IntegrationState.CreatingAccount && (
                  <LoadingScreen message="Please wait while we setup your new account..." />
                )}
            </>
          )}

          {createAccountResponse && (
            <>
              {createAccountResponse?.error && (
                <>
                  {createAccountResponse?.error?.includes &&
                  createAccountResponse?.error?.includes(
                    'bad_verification_code',
                  ) ? (
                    <>
                      Auth Code is expired, please retry the connection and
                      re-authenticate.
                    </>
                  ) : (
                    <CenteredContent>
                      <strong>Error: {createAccountResponse?.error}</strong>
                    </CenteredContent>
                  )}
                </>
              )}
              {createAccountResponse?.success && state?.mode === 'revive' && (
                <CenteredContent>
                  <strong>
                    Account Successfully Connected. Id:{' '}
                    {createAccountResponse?.id}
                  </strong>
                </CenteredContent>
              )}

              {createAccountResponse?.success && state?.mode === 'new' && (
                <CenteredContent>
                  <strong>
                    New Account Successfully Created. Id:{' '}
                    {createAccountResponse?.id}
                  </strong>
                </CenteredContent>
              )}
            </>
          )}
        </PageLayout>
      </ErrorBoundary>
      <ToastContainer />
    </Suspense>
  );
};

export default NewAccountIntegration;
